import { duplicateTimeRangeInputValue, timeRangeInputValueToAbsolute } from '@chronosphereio/core';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { DEFAULT_TIME_RANGE_INPUT_VALUE } from '@/core';
import { useAutoRefresh } from './auto-refresh';
import { type BackwardsCompatibilityProps, useBackwardsCompatibility } from './backwards-compatibility';
import { useInMemoryTimeRangeStorage } from './in-memory-time-range-storage';
import { usePinnedTimeRange, usePinnedTimeRangeStatus } from './pinned-time-range-hooks';
import { useSyncPinnedTimeRange } from './sync-pinned-time-range';
import { useRegisterTimeRangeProvider } from './TimeRangeProviderRegistry';
import { type TimeRangeProviderApi, type TimeRangeProviderOptions } from './time-range-api';
import { useUrlTimeRangeStorage } from './url-time-range-storage';

/**
 * Creates a `TimeRangeProvider` context object based on the `props` provided. This is used internally by the provider
 * and so in most cases, should not be used directly unless you manually need to create a context object that should
 * conditionally be used.
 */
export function useCreateTimeRangeContext(initialOptions: Partial<TimeRangeProviderOptions>): TimeRangeProviderApi {
  const [options, _setOptions] = useState<TimeRangeProviderOptions>(() => ({
    enableUrlParams: initialOptions.enableUrlParams ?? false,
    urlParamsScope: initialOptions.urlParamsScope ?? '',
    enablePinning: initialOptions.enablePinning ?? true,
    defaultTimeRange: initialOptions.defaultTimeRange ?? DEFAULT_TIME_RANGE_INPUT_VALUE,
    initialRefreshInterval: initialOptions.initialRefreshInterval,
  }));

  // Get both storage immplementations, then pick one based on the options
  const inMemoryStorage = useInMemoryTimeRangeStorage(options);
  const urlStorage = useUrlTimeRangeStorage(options);
  const storage = options.enableUrlParams ? urlStorage : inMemoryStorage;

  // Recalculate absolute time range whenever value in storage changes
  const absoluteTimeRange = useMemo(() => timeRangeInputValueToAbsolute(storage.timeRange), [storage.timeRange]);

  const { pinnedStatus } = usePinnedTimeRangeStatus();
  const { setPinnedTimeRange } = usePinnedTimeRange();
  const { setTimeRange: setStorageTimeRange } = storage;
  const setTimeRange: TimeRangeProviderApi['setTimeRange'] = useCallback(
    (next) => {
      // Set in storage (the 'pushIn' argument is used by URL storage but otherwise ignored)
      setStorageTimeRange(next, 'pushIn');

      // Also update pinned value if we're in pinned status
      if (options.enablePinning && pinnedStatus === 'pinned') {
        setPinnedTimeRange(next);
      }
    },
    [options.enablePinning, pinnedStatus, setPinnedTimeRange, setStorageTimeRange]
  );

  // Keep track of refreshes purely for the `refreshKey` string that's returned from this hook (so that if an absolute
  // time range is refreshed, the `refreshKey` will change and any consumers that depend on it will re-run)
  const [refreshCount, setRefreshCount] = useState(0);
  const refresh: TimeRangeProviderApi['refresh'] = useCallback(() => {
    const copy = duplicateTimeRangeInputValue(storage.timeRange);
    setTimeRange(copy);
    setRefreshCount((current) => current + 1);
  }, [setTimeRange, storage.timeRange]);

  useSyncPinnedTimeRange(storage, options.enablePinning);

  // Automatically refresh the time range as necessary
  const { refreshInterval, refreshIntervalInMs, setRefreshInterval } = useAutoRefresh({
    initialRefreshInterval: options.initialRefreshInterval,
    refresh,
    enabledURLParams: options.enableUrlParams,
  });

  // Register with `TimeRangeProviderRegistry` to expose an imperative API outside of the provider
  useRegisterTimeRangeProvider(storage.timeRange, absoluteTimeRange, options);

  return useMemo(
    () => ({
      timeRange: storage.timeRange,
      absoluteTimeRange,
      setTimeRange,
      refresh,
      refreshInterval,
      refreshIntervalInMs,
      setRefreshInterval,
      refreshKey: `${refreshCount}:${absoluteTimeRange.start.valueOf()}:${absoluteTimeRange.end.valueOf()}`,
      options,
    }),
    [
      absoluteTimeRange,
      options,
      refresh,
      refreshCount,
      refreshInterval,
      refreshIntervalInMs,
      setRefreshInterval,
      setTimeRange,
      storage.timeRange,
    ]
  );
}

export const TimeRangeProviderContext = createContext<TimeRangeProviderApi | undefined>(undefined);

export interface TimeRangeProviderProps extends Partial<TimeRangeProviderOptions>, BackwardsCompatibilityProps {
  children?: ReactNode;
}

/**
 * Provider for managing the time range at runtime.
 */
export function TimeRangeProvider({ children, ...props }: TimeRangeProviderProps) {
  const ctx = useCreateTimeRangeContext(props);

  // Support a few props from the v1 provider temporarily until we can do alternate implementations in consumers
  useBackwardsCompatibility(ctx, props);

  return <TimeRangeProviderContext.Provider value={ctx}>{children}</TimeRangeProviderContext.Provider>;
}

/**
 * Gets the time range provider API for interacting with the time range at runtime.
 */
export function useTimeRange(): TimeRangeProviderApi {
  const ctx = useContext(TimeRangeProviderContext);
  if (ctx === undefined) {
    throw new Error('No TimeRangeContext found. Did you forget a Provider?');
  }
  return ctx;
}
